import React, { useState } from 'react';
import SEO from '../SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Activity, BarChart2, CheckCircle, Check, X, ChevronDown, ChevronUp, Globe, MousePointer, Shield, TrendingUp, Zap, Target, Layout, Clock, Play, Server, Lock, Code , Star} from 'lucide-react';

interface LandingPageProps {
    onLogin?: () => void;
    onNavigate?: (page: string) => void;
}

const DeOrganicWebsiteTraffic: React.FC<LandingPageProps> = ({ onLogin, onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);

    // Schema.org Structured Data
    const schema = [
        {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Generator für organischen Website-Traffic",
            "description": "Service für organischen Traffic zur SEO-Analyse, CTR-Auswertung und GA4-sichtbaren Messung von Engagement mit Residential-IP-Routing.",
            "brand": { "@type": "Brand", "name": "Traffic Creator" }
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Heim", "item": "https://traffic-creator.com" },
                { "@type": "ListItem", "position": 2, "name": "Organischer Website-Traffic", "item": "https://traffic-creator.com/de/organischen-traffic-kaufen" }
            ]
        }
    ];

    const benefits = [
        {
            icon: <Search className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Organische CTR analysieren",
            desc: "Leiten Sie Besucher über suchähnliche Pfade, um CTR, Sitzungstiefe und keywordbezogenes Engagement in Analytics auszuwerten."
        },
        {
            icon: <Clock className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Verweildauer erhöhen",
            desc: "Besucher bleiben minutenlang auf Ihrer Website, scrollen durch Inhalte und interagieren, was die Absprungraten drastisch reduziert."
        },
        {
            icon: <Globe className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Lokale Traffic-Abdeckung",
            desc: "Targeten Sie Traffic aus bestimmten Städten oder Regionen, um lokale Engagement-Qualität zwischen Märkten zu vergleichen."
        },
        {
            icon: <Shield className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Konfigurierbares Browsing-Verhalten",
            desc: "Mausbewegungen, Scrollen und Klicks werden randomisiert, um realistische Browsing-Muster für Analytics-Tests zu modellieren."
        }
    ];

    const comparisons = [
        { feature: "Traffic-Quelle", us: "Echte Wohn-IPs", them: "Rechenzentrum-Proxies" },
        { feature: "Verhalten", us: "Scrollen, Klicken, Lesen", them: "Statische Aufrufe" },
        { feature: "Google Analytics", us: "100% erkannt", them: "Oft gefiltert / Bot" },
        { feature: "SEO-Analyse", us: "GA4-sichtbare Engagement-Daten", them: "Neutrale oder verrauschte Daten" },
        { feature: "AdSense Safe", us: "Ja, verifiziert", them: "Hohes Sperrungsrisiko" },
    ];

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 font-sans overflow-x-hidden">
            <SEO
                title="Organischen Website-Traffic kaufen - Echte Wohn-IPs"
                description="Kaufen Sie organisch wirkenden Website-Traffic über Suchmaschinenreferrals für CTR-Auswertung, Engagement-Messung und SEO-Analysen."
                keywords="organischer website traffic 2026, organischen traffic kaufen, seo traffic, ctr analyse, suchsichtbarkeit analysieren, echte besucher, ki traffic bot"
                schema={schema}
                type="product"
            />

            {/* Hreflang Tags */}

            {/* Navigation */}
            <nav className="fixed w-full top-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md border-b border-gray-100/50">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                        <Link to="/" className="flex items-center gap-2">
                            <span className="text-2xl font-black text-[#ff5a0a] tracking-tight group-hover:scale-105 transition-transform">TRAFFIC</span>
                            <span className="hidden sm:inline-block text-xs font-bold bg-black text-white px-2 py-1 rounded-sm uppercase tracking-wide">Creator</span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-8">
                        <div className="hidden md:flex gap-8 text-sm font-bold uppercase tracking-wide text-gray-500">
                            <a href="#how-it-works" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Funktionsweise
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <a href="#benefits" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Merkmale
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <Link to="/#pricing" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Preise
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </Link>
                        </div>
                        <Link to="/login" className="bg-black text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#ff5a0a] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center rounded-sm">
                            Dashboard Login
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-32 px-6 relative overflow-hidden bg-white">
                <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none rounded-bl-[100px]"></div>

                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
                    <div className="animate-in fade-in slide-in-from-bottom-8 duration-700">
                        <div className="inline-flex items-center gap-2 bg-orange-50 text-[#ff5a0a] border border-orange-100 px-4 py-2 text-xs font-bold uppercase tracking-widest mb-8 rounded-full shadow-sm">
                            <Zap size={14} className="fill-[#ff5a0a]" /> SEO Intelligenz-Engine
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[0.9] mb-8 text-gray-900">
                            ANALYSIEREN SIE <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff5a0a] to-orange-500">ORGANISCHE</span><br />TRAFFIC-QUALITÄT.
                        </h1>
                        <p className="text-xl text-gray-500 mb-10 max-w-lg leading-relaxed font-medium">
                            Ein Generator für organischen Website-Traffic für SEO-Profis, die CTR, Verweildauer und GA4-sichtbares Engagement auswerten möchten.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/signup" className="group bg-[#ff5a0a] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-black transition-all shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                                SEO-Kampagne starten
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <a href="#demo" className="bg-white border border-gray-200 text-gray-900 px-8 py-4 text-sm font-bold uppercase tracking-wider hover:border-[#ff5a0a] hover:text-[#ff5a0a] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 rounded-sm group">
                                <Play size={16} className="group-hover:fill-[#ff5a0a]" /> Live-Demo ansehen
                            </a>
                        </div>
                    </div>

                    {/* Visual: Abstract SEO Graph */}
                    <div className="relative h-[550px] perspective-1000 group">
                        <div className="absolute inset-0 bg-white border border-gray-200 shadow-2xl rounded-2xl p-6 overflow-hidden transform transition-transform duration-700 group-hover:-translate-y-2">
                            <div className="border-b border-gray-100 pb-4 mb-6 flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400/80"></div>
                            </div>

                            <div className="space-y-6">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className={`p-5 rounded-lg border transition-all duration-500 ${i === 1 ? 'border-[#ff5a0a]/20 bg-orange-50/30' : 'border-gray-50 bg-white'}`}>
                                        {i === 1 && <div className="absolute top-0 left-0 w-1 h-full bg-[#ff5a0a]"></div>}
                                        <div className="w-3/4 h-5 rounded-sm mb-3 bg-gray-100"></div>
                                        <div className="space-y-2">
                                            <div className="w-full h-2 bg-gray-100 rounded-sm"></div>
                                            <div className="w-5/6 h-2 bg-gray-100 rounded-sm"></div>
                                        </div>
                                        {i === 1 && (
                                            <div className="mt-4 flex items-center gap-2 text-[#ff5a0a] text-xs font-bold uppercase tracking-wider animate-pulse">
                                                <TrendingUp size={12} /> Traffic quality • CTR analysis active
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="absolute bottom-6 right-6 bg-gray-900/95 backdrop-blur-md text-white p-5 rounded-xl shadow-2xl z-20 border border-gray-800">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#ff5a0a] mb-2">Leistung</div>
                                <div className="text-4xl font-black mb-1">18.4%</div>
                                <div className="text-xs text-gray-400 font-medium">Organische CTR (7 Tage)</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Grid */}
            <section id="benefits" className="py-32 px-6 bg-white relative">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-24">
                        <h2 className="text-4xl font-black uppercase tracking-tight mb-6">Warum Traffic-Qualitaet messen?</h2>
                        <div className="w-16 h-1.5 bg-[#ff5a0a] mx-auto mb-8 rounded-full"></div>
                        <p className="text-gray-500 max-w-2xl mx-auto text-lg leading-relaxed">
                            Suchalgorithmen entwickeln sich weiter. Backlinks reichen nicht mehr aus. Sie brauchen <span className="text-gray-900 font-bold">Nutzersignale</span>. Traffic Creator erzeugt die perfekte Verweildauer.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                        {benefits.map((b, i) => (
                            <div key={i} className="flex gap-8 p-10 border border-gray-100 hover:border-[#ff5a0a]/30 bg-white hover:bg-orange-50/20 shadow-sm transition-all duration-300 group rounded-2xl relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1 h-0 bg-[#ff5a0a] group-hover:h-full transition-all duration-300"></div>
                                <div className="shrink-0">
                                    <div className="bg-orange-50 w-16 h-16 flex items-center justify-center rounded-2xl group-hover:bg-[#ff5a0a] transition-all text-[#ff5a0a] group-hover:text-white">
                                        {b.icon}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#ff5a0a] transition-colors">{b.title}</h3>
                                    <p className="text-gray-500 leading-relaxed font-medium">{b.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Premium Comparison */}
            <section className="py-32 px-6 bg-white">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-black uppercase tracking-tight mb-4">Qualität zählt</h2>
                        <p className="text-gray-500">Riskieren Sie nicht Ihre Domain-Autorität mit billigem Bot-Traffic.</p>
                    </div>

                    <div className="bg-white shadow-[0_10px_60px_rgba(0,0,0,0.05)] rounded-2xl overflow-hidden border border-gray-100">
                        <div className="grid grid-cols-3 bg-gray-900 text-white p-6 text-xs font-bold uppercase tracking-widest">
                            <div className="pl-4">Merkmal</div>
                            <div className="text-[#ff5a0a] flex items-center gap-2"><Zap size={14} className="fill-[#ff5a0a]" /> Traffic Creator</div>
                            <div className="text-gray-500">Billige Bots</div>
                        </div>
                        <div className="divide-y divide-gray-50">
                            {comparisons.map((c, i) => (
                                <div key={i} className="grid grid-cols-3 p-6 items-center text-sm font-medium hover:bg-gray-50 transition-colors">
                                    <div className="font-bold text-gray-900 pl-4">{c.feature}</div>
                                    <div className="flex items-center gap-3 text-[#ff5a0a] font-bold">
                                        <CheckCircle size={18} className="ml-4" /> {c.us}
                                    </div>
                                    <div className="text-gray-400 flex items-center gap-3 pl-4">
                                        <X size={16} /> {c.them}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-24 px-6 bg-gray-50 border-y border-gray-100">
                <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <h2 className="text-3xl font-black uppercase tracking-tight mb-6">Wofür organischer Traffic sinnvoll ist</h2>
                        <p className="text-gray-600 leading-relaxed font-medium mb-5">
                            Organischer Website-Traffic sollte nicht als Ranking-Versprechen verstanden werden. Für SEO-Teams ist er vor allem ein Messinstrument:
                            Sie sehen, ob Landingpages Besucher halten, ob interne Links geklickt werden und ob bestimmte Länder oder Geräte auffällig andere
                            Engagement-Werte erzeugen. Diese Daten helfen, Inhalte, Seitenstruktur und Conversion-Pfade gezielter zu verbessern.
                        </p>
                        <p className="text-gray-600 leading-relaxed font-medium">
                            Traffic Creator setzt deshalb auf transparente Kampagnenparameter statt auf pauschale Garantien. Sie bestimmen Referrer,
                            Sitzungsdauer, Scroll-Tiefe, Seitenpfade und Geo-Ziele. Danach prüfen Sie die Ergebnisse in GA4, Search Console und eigenen
                            Dashboards, bevor Sie Budgets erhöhen oder Inhalte überarbeiten.
                        </p>
                        <p className="text-gray-600 leading-relaxed font-medium mt-5">
                            Besonders wertvoll ist der Vergleich zwischen mehreren Seitentypen: Startseite, Produktseite, Blogartikel und lokale Landingpage
                            reagieren oft sehr unterschiedlich auf dieselbe Besucherquelle. Wenn Sie diese Unterschiede sauber protokollieren, erkennen Sie
                            schneller, welche Seiten klare Botschaften haben und welche Inhalte Besucher noch nicht ausreichend führen.
                        </p>
                    </div>
                    <div>
                        <h2 className="text-3xl font-black uppercase tracking-tight mb-6">Was vor dem Start geprüft werden sollte</h2>
                        <ul className="space-y-4 text-gray-600 font-medium leading-relaxed">
                            <li className="flex gap-3"><CheckCircle className="w-5 h-5 text-[#ff5a0a] shrink-0 mt-1" /> Definieren Sie Zielseiten, Kontrollseiten und eine klare Baseline für Sitzungsdauer, Bounce Rate und interne Klicks.</li>
                            <li className="flex gap-3"><CheckCircle className="w-5 h-5 text-[#ff5a0a] shrink-0 mt-1" /> Starten Sie mit moderaten Volumen, damit Analytics-Trends nachvollziehbar bleiben und nicht durch abrupte Sprünge verzerrt werden.</li>
                            <li className="flex gap-3"><CheckCircle className="w-5 h-5 text-[#ff5a0a] shrink-0 mt-1" /> Vergleichen Sie Traffic-Qualität pro Land, Gerät und Referrer, bevor Sie daraus Content- oder UX-Entscheidungen ableiten.</li>
                            <li className="flex gap-3"><CheckCircle className="w-5 h-5 text-[#ff5a0a] shrink-0 mt-1" /> Dokumentieren Sie jede Kampagne, damit spätere SEO-Auswertungen zwischen echten Content-Änderungen und Traffic-Tests unterscheiden können.</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 px-6 bg-white border-t border-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 px-4">
                        <span className="text-[#ff5a0a] font-bold tracking-wider uppercase text-xs mb-4 block">Testimonials</span>
                        <h2 className="text-4xl font-black text-gray-900 mb-6">Erfolgsgeschichten</h2>
                        <p className="text-xl text-gray-500 max-w-2xl mx-auto">
                            Entdecken Sie, wie Profis die SERPs mit unserem organischen Traffic dominieren.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=lukas_w`} alt="Lukas Weber" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Lukas Weber</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">SEO-Agentur Inhaber</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"Die CTR-Optimierung von Traffic Creator ist ein Gamechanger. Wir haben die Rankings unserer Kunden in extrem unkämpften Nischen drastisch verbessert."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=julia_h`} alt="Julia Hoffmann" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Julia Hoffmann</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">Online Marketing Managerin</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"Endlich echter Traffic, der nicht von Google als Bot-Spam aussortiert wird. Die Verweildauer und Interaktionsraten sind fantastisch."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=max_b`} alt="Maximilian Becker" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Maximilian Becker</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">E-Commerce Leiter</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"Wir nutzen die Software für lokales SEO und pushen unsere Maps-Einträge effektiv. Die lokalen IP-Adressen machen hier den kompletten Unterschied."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-32 px-6 bg-[#ff5a0a] relative overflow-hidden">
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-8 text-white">Jetzt Ranking starten</h2>
                    <p className="text-xl text-white/90 mb-12 font-medium max-w-2xl mx-auto">
                        Ihre Konkurrenten tun es bereits. Gleichen Sie die Bedingungen mit dem weltweit fortschrittlichsten Generator für organischen Traffic aus.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <Link to="/signup" className="bg-white text-black px-12 py-5 text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-all shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                            Kostenlose Testversion <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-black text-white py-20 px-6 border-t border-gray-900 text-center">
                <div className="max-w-7xl mx-auto">
                    <div className="text-3xl font-black mb-8">TRAFFIC CREATOR</div>
                    <p className="text-gray-500 max-w-md mx-auto mb-12">
                        Der Industriestandard für SEO-Traffic-Generierung. Wir helfen Agenturen und Website-Besitzern, ihre Suchsichtbarkeitssignale sicher zu verbessern.
                    </p>
                    <div className="text-xs text-gray-600">
                        © {new Date().getFullYear()} Traffic Creator. Alle Rechte vorbehalten.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default DeOrganicWebsiteTraffic;
